import { create } from "zustand";
import {
  generateSessionId,
  resolveFlowSteps,
  type AnyVehicle,
  type Branch,
  type Contact,
  type ContactInfoFields,
  type GarageConfig,
  type Service,
  type StepId,
  type TimeSlot,
  type Vehicle,
} from "@autopic/shared";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

export interface SessionState {
  // identity
  sessionId: string;
  garageId: string | null;
  singleGaragePreview: boolean;
  config: GarageConfig | null;
  configStatus: LoadStatus;
  configError: string | null;

  // collected data
  vehicle: AnyVehicle | null;
  vehicleRegistration: string | null;
  /** True when the vehicle came from a successful DVLA-style lookup. */
  vehicleFound: boolean;
  contact: Contact;
  postcode: string | null;
  contactAddress: string | null;
  contactCity: string | null;
  contactPostcode: string | null;
  contactInfoFields: ContactInfoFields | null;
  branches: Branch[];
  branchId: string | null;
  selectedServiceIds: string[];
  availableServices: Service[];
  slot: TimeSlot | null;
  notes: string | null;

  // lead lifecycle
  leadId: string | null;

  // navigation
  stepIndex: number;
  /** Highest step the user has reached (so Change links can jump back/forward). */
  maxStepIndex: number;
  /**
   * When a user edits via a Change link, we remember where to bounce back to
   * (the summary) so completing the edited step returns there instead of
   * re-walking the whole flow.
   */
  returnStepIndex: number | null;
  bookingReference: string | null;
  bookingTotalPrice: number | null;

  // actions
  init: (garageId: string, singleGaragePreview?: boolean) => void;
  setConfig: (config: GarageConfig) => void;
  setConfigStatus: (status: LoadStatus, error?: string | null) => void;

  setVehicle: (vehicle: AnyVehicle | null, opts?: { found?: boolean }) => void;
  setVehicleRegistration: (reg: string | null) => void;
  setContact: (patch: Partial<Contact>) => void;
  setPostcode: (postcode: string | null) => void;
  setContactAddress: (address: string | null) => void;
  setContactCity: (city: string | null) => void;
  setContactPostcode: (postcode: string | null) => void;
  setContactInfoFields: (fields: ContactInfoFields | null) => void;
  setBranches: (branches: Branch[]) => void;
  setBranchId: (branchId: string | null) => void;
  toggleService: (
    serviceId: string,
    combinable: boolean,
    lockedIds?: string[],
  ) => void;
  setServices: (ids: string[]) => void;
  setAvailableServices: (services: Service[]) => void;
  setSlot: (slot: TimeSlot | null) => void;
  setNotes: (notes: string | null) => void;
  setLeadId: (id: string | null) => void;

  goToStep: (index: number) => void;
  /** Jump to a step for editing, remembering where to return (e.g. summary). */
  goToForEdit: (index: number, returnTo: number) => void;
  next: () => void;
  back: () => void;
  setBookingReference: (ref: string) => void;
  setBookingTotalPrice: (price: number) => void;
  reset: () => void;
}

function makeInitial() {
  return {
    sessionId: generateSessionId(),
    garageId: null as string | null,
    singleGaragePreview: false,
    config: null as GarageConfig | null,
    configStatus: "idle" as LoadStatus,
    configError: null as string | null,
    vehicle: null as AnyVehicle | null,
    vehicleRegistration: null as string | null,
    vehicleFound: false,
    contact: {} as Contact,
    postcode: null as string | null,
    contactAddress: null as string | null,
    contactCity: null as string | null,
    contactPostcode: null as string | null,
    contactInfoFields: null as ContactInfoFields | null,
    branches: [] as Branch[],
    branchId: null as string | null,
    selectedServiceIds: [] as string[],
    availableServices: [] as Service[],
    slot: null as TimeSlot | null,
    notes: null as string | null,
    leadId: null as string | null,
    stepIndex: 0,
    maxStepIndex: 0,
    returnStepIndex: null as number | null,
    bookingReference: null as string | null,
    bookingTotalPrice: null as number | null,
  };
}

export const useSession = create<SessionState>((set, get) => ({
  ...makeInitial(),

  init(garageId, singleGaragePreview = false) {
    singleGaragePreview = garageId === "demo-sandbox" && singleGaragePreview;
    const cur = get();
    // Preserve a journey until its garage or sandbox preview variant changes.
    if (
      cur.garageId === garageId &&
      cur.singleGaragePreview === singleGaragePreview &&
      cur.config
    )
      return;
    set({ ...makeInitial(), garageId, singleGaragePreview });
  },

  setConfig(config) {
    // Website previews mirror SGG's six customer steps while still using the
    // existing sandbox API and its first mock branch. Never override live data.
    if (
      get().singleGaragePreview &&
      config.sandbox &&
      config.id === "demo-sandbox"
    ) {
      config = {
        ...config,
        flow: {
          ...config.flow,
          steps: [
            "registration",
            "vehicle",
            "services",
            "datetime",
            "details",
            "summary",
            "confirmation",
          ],
          contactStrategy: "both",
          contactFirst: false,
          branchFirst: false,
          locationEnabled: false,
        },
        branches: config.branches.slice(0, 1),
        sessionTimer: { ...config.sessionTimer, enabled: false },
        trust: { ...config.trust, enabled: false },
      };
    }
    // Store the *effective* step order (location dropped for fixed installs,
    // mot dropped without an MOT service) so every
    // index-based navigation below works off one consistent list.
    let effectiveSteps = resolveFlowSteps(config);
    // Garage Hive needs the branch before it can look up the vehicle/services.
    if (
      !config.sandbox &&
      config.flow.locationEnabled &&
      config.branches.length > 1
    ) {
      effectiveSteps = [
        "location",
        ...effectiveSteps.filter((step) => step !== "location"),
      ];
    }
    const effective: GarageConfig = {
      ...config,
      flow: { ...config.flow, steps: effectiveSteps },
    };
    const fixedBranch =
      config.branches.length === 1 || !config.flow.locationEnabled
        ? (config.branches[0] ?? null)
        : null;
    set({
      config: effective,
      configStatus: "ready",
      configError: null,
      branches: config.branches,
      branchId: fixedBranch?.id ?? null,
    });
  },
  setConfigStatus(status, error = null) {
    set({ configStatus: status, configError: error });
  },

  setVehicle(vehicle, opts) {
    set({ vehicle, vehicleFound: opts?.found ?? false });
  },
  setVehicleRegistration(reg) {
    set({ vehicleRegistration: reg });
  },
  setContact(patch) {
    set((s) => ({ contact: { ...s.contact, ...patch } }));
  },
  setPostcode(postcode) {
    set({ postcode });
  },
  setContactAddress(contactAddress) {
    set({ contactAddress });
  },
  setContactCity(contactCity) {
    set({ contactCity });
  },
  setContactPostcode(contactPostcode) {
    set({ contactPostcode });
  },
  setContactInfoFields(contactInfoFields) {
    set({ contactInfoFields });
  },
  setBranches(branches) {
    set({ branches });
  },
  setBranchId(branchId) {
    // Changing branch invalidates a previously chosen slot.
    set((s) => ({
      branchId,
      slot: s.branchId === branchId ? s.slot : null,
      contactInfoFields: s.branchId === branchId ? s.contactInfoFields : null,
    }));
  },
  toggleService(serviceId, combinable, lockedIds = []) {
    set((s) => {
      // Locked ids (the booked MOT) can't be toggled off and survive a
      // non-combinable replacement.
      if (lockedIds.includes(serviceId)) return {};
      const has = s.selectedServiceIds.includes(serviceId);
      if (has) {
        return {
          selectedServiceIds: s.selectedServiceIds.filter(
            (id) => id !== serviceId,
          ),
          slot: null,
          contactInfoFields: null,
        };
      }
      // Non-combinable selection replaces the other add-ons; otherwise add.
      if (!combinable) {
        const kept = s.selectedServiceIds.filter((id) =>
          lockedIds.includes(id),
        );
        return {
          selectedServiceIds: [...kept, serviceId],
          slot: null,
          contactInfoFields: null,
        };
      }
      return {
        selectedServiceIds: [...s.selectedServiceIds, serviceId],
        slot: null,
        contactInfoFields: null,
      };
    });
  },
  setServices(ids) {
    set({ selectedServiceIds: ids, slot: null, contactInfoFields: null });
  },
  setAvailableServices(availableServices) {
    set({ availableServices });
  },
  setSlot(slot) {
    set({ slot });
  },
  setNotes(notes) {
    set({ notes });
  },
  setLeadId(id) {
    set({ leadId: id });
  },

  goToStep(index) {
    set((s) => ({
      stepIndex: index,
      maxStepIndex: Math.max(s.maxStepIndex, index),
      returnStepIndex: null,
    }));
  },
  goToForEdit(index, returnTo) {
    set((s) => ({
      stepIndex: index,
      maxStepIndex: Math.max(s.maxStepIndex, index),
      returnStepIndex: returnTo,
    }));
  },
  next() {
    set((s) => {
      const steps = s.config?.flow.steps ?? [];
      // If the user was editing via a Change link, bounce back to where they
      // started (the summary) on the next confirm — but only if it's ahead.
      if (s.returnStepIndex !== null && s.returnStepIndex > s.stepIndex) {
        // Service/vehicle/location edits can invalidate availability and the
        // garage's required contact fields. Rewalk those steps before review.
        const datetimeIndex = steps.indexOf("datetime");
        if (!s.slot && datetimeIndex > s.stepIndex) {
          return {
            stepIndex: datetimeIndex,
            maxStepIndex: Math.max(s.maxStepIndex, datetimeIndex),
            returnStepIndex: null,
          };
        }
        const target = Math.min(s.returnStepIndex, steps.length - 1);
        return {
          stepIndex: target,
          maxStepIndex: Math.max(s.maxStepIndex, target),
          returnStepIndex: null,
        };
      }
      const nextIndex = Math.min(s.stepIndex + 1, steps.length - 1);
      return {
        stepIndex: nextIndex,
        maxStepIndex: Math.max(s.maxStepIndex, nextIndex),
        returnStepIndex: null,
      };
    });
  },
  back() {
    set((s) => ({
      stepIndex: Math.max(s.stepIndex - 1, 0),
      returnStepIndex: null,
    }));
  },
  setBookingReference(ref) {
    set({ bookingReference: ref });
  },
  setBookingTotalPrice(bookingTotalPrice) {
    set({ bookingTotalPrice });
  },
  reset() {
    const garageId = get().garageId;
    set({ ...makeInitial(), garageId });
  },
}));

/** Derive the current StepId from config + index. */
export function currentStepId(state: SessionState): StepId | null {
  const steps = state.config?.flow.steps;
  if (!steps || steps.length === 0) return null;
  return steps[Math.min(state.stepIndex, steps.length - 1)] ?? null;
}

/** Resolve the confirmed Vehicle (with registration) for booking, if complete. */
export function resolveVehicle(
  state: SessionState,
): Vehicle | (AnyVehicle & { registration?: string }) | null {
  if (!state.vehicle) return null;
  if ("registration" in state.vehicle && state.vehicle.registration) {
    return state.vehicle;
  }
  if (state.vehicleRegistration) {
    return { ...state.vehicle, registration: state.vehicleRegistration };
  }
  return state.vehicle;
}
